import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Navigation = () => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const NavLink = ({ to, children, testId }) => (
    <Link
      to={to}
      data-testid={testId}
      onClick={closeMobileMenu}
      className={`font-ui text-sm uppercase tracking-widest transition-colors ${
        isActive(to) ? 'text-gold' : 'text-charcoal/80 hover:text-gold'
      }`}
    >
      {children}
    </Link>
  );

  return (
    <nav data-testid="main-navigation" className="sticky top-0 z-50 w-full bg-parchment/95 backdrop-blur-sm border-b border-charcoal/5">
      <div className="container mx-auto px-4 md:px-12">
        <div className="flex justify-between items-center py-4 md:py-6">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 md:gap-4 hover:opacity-80 transition-opacity duration-300">
            <img 
              src="/logo.png" 
              alt="Logo" 
              className="h-10 w-10 md:h-12 md:w-12 object-contain"
            />
            <span className="font-heading text-lg md:text-2xl lg:text-3xl italic text-charcoal hidden sm:block">
              Die Afrikaner se Goue Kalf
            </span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            <NavLink to="/" testId="nav-home">Tuis</NavLink>
            <NavLink to="/about" testId="nav-about">Oor die Boek</NavLink>
            <NavLink to="/author" testId="nav-author">Outeur</NavLink>
            <NavLink to="/excerpts" testId="nav-excerpts">Uittreksels</NavLink>
            <NavLink to="/purchase" testId="nav-purchase">Koop</NavLink>
            
            {user ? (
              <>
                <NavLink to="/reader" testId="nav-reader">Lees</NavLink>
                <NavLink to="/dashboard" testId="nav-dashboard">Biblioteek</NavLink>
                {isAdmin && (
                  <NavLink to="/admin" testId="nav-admin">Admin</NavLink>
                )}
                <button
                  onClick={logout}
                  data-testid="nav-logout"
                  className="font-ui text-sm uppercase tracking-widest text-charcoal/80 hover:text-gold transition-colors"
                >
                  Teken Uit
                </button>
              </>
            ) : (
              <Link
                to="/login"
                data-testid="nav-login"
                className="bg-charcoal text-parchment hover:bg-charcoal/80 rounded-sm px-6 py-3 font-ui uppercase tracking-widest text-sm transition-colors duration-300"
              >
                Teken Aan
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-charcoal hover:text-gold transition-colors"
            aria-label="Menu"
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden pb-6 border-t border-charcoal/10 pt-4">
            <div className="flex flex-col gap-4">
              <NavLink to="/" testId="nav-home-mobile">Tuis</NavLink>
              <NavLink to="/about" testId="nav-about-mobile">Oor die Boek</NavLink>
              <NavLink to="/author" testId="nav-author-mobile">Outeur</NavLink>
              <NavLink to="/excerpts" testId="nav-excerpts-mobile">Uittreksels</NavLink>
              <NavLink to="/purchase" testId="nav-purchase-mobile">Koop</NavLink>
              
              {user ? (
                <>
                  <NavLink to="/reader" testId="nav-reader-mobile">Lees</NavLink>
                  <NavLink to="/dashboard" testId="nav-dashboard-mobile">Biblioteek</NavLink>
                  {isAdmin && (
                    <NavLink to="/admin" testId="nav-admin-mobile">Admin</NavLink>
                  )}
                  <button
                    onClick={() => { logout(); closeMobileMenu(); }}
                    data-testid="nav-logout-mobile"
                    className="font-ui text-sm uppercase tracking-widest text-charcoal/80 hover:text-gold transition-colors text-left"
                  >
                    Teken Uit
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={closeMobileMenu}
                  data-testid="nav-login-mobile"
                  className="bg-charcoal text-parchment hover:bg-charcoal/80 rounded-sm px-6 py-3 font-ui uppercase tracking-widest text-sm transition-colors duration-300 text-center mt-2"
                >
                  Teken Aan
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
